import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useToast } from "@/components/ui/use-toast";
import { ProductStoreCategory } from "../api/fetchProductStoreCategories/types";
import { createCategoryColumns } from "../components/CategoryColumns/CategoryColumns";
import { deleteProductStoreCategoryAction } from "../slices/productStoreCategoriesSlice";
import { useProductStoreCategories } from "./useProductStoreCategories";

export function useCategoryList() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [refreshCounter, setRefreshCounter] = useState(0);
  const categoriesQuery = useProductStoreCategories(refreshCounter);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<ProductStoreCategory | null>(null);
  const [deleteDialogState, setDeleteDialogState] = useState({
    isOpen: false,
    categoryId: null as string | null,
    categoryName: "",
  });

  const refreshList = useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  const handleCreateCategory = useCallback(() => {
    setEditingCategory(null);
    setIsDialogOpen(true);
  }, []);

  const handleEditCategory = useCallback((category: ProductStoreCategory) => {
    setEditingCategory(category);
    setIsDialogOpen(true);
  }, []);

  const handleDeleteCategory = useCallback((category: ProductStoreCategory) => {
    setDeleteDialogState({
      isOpen: true,
      categoryId: category.id,
      categoryName: category.name,
    });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogState({
      isOpen: false,
      categoryId: null,
      categoryName: "",
    });
  }, []);

  const closeCategoryDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialogState.categoryId) return;

    try {
      await dispatch(
        deleteProductStoreCategoryAction(deleteDialogState.categoryId),
      );
      toast({
        title: "Success",
        description: "Category deleted successfully",
        variant: "default",
      });
      closeDeleteDialog();
      refreshList();
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to delete category",
        variant: "destructive",
      });
    }
  }, [
    dispatch,
    deleteDialogState.categoryId,
    closeDeleteDialog,
    refreshList,
    toast,
  ]);

  const columns = useMemo(
    () => createCategoryColumns(handleEditCategory, handleDeleteCategory),
    [handleEditCategory, handleDeleteCategory],
  );

  return {
    ...categoriesQuery,
    columns,
    isDialogOpen,
    editingCategory,
    deleteDialogState,
    handleCreateCategory,
    closeCategoryDialog,
    closeDeleteDialog,
    handleConfirmDelete,
    refreshList,
  };
}
